import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import CommonStyles from "@components/CommonStyles";
import { categories } from "@base/constants";
import { formatVnd } from "@base/modules/appointment/bookingCommon";
import { voucherError } from "@base/modules/appointment/regularBookingRules";
import { IPromotion } from "@base/modules/promotion/interface/promotion.interface";
import promotionService from "@base/modules/promotion/promotionService";
import { RootState } from "@base/store/store";

interface IProps {
  open: boolean;
  toggle: () => void;
  online: boolean;
  siteCode: string;
  lang: string;
  current: IPromotion | null;
  /** null removes the code. */
  onApplied: (voucher: IPromotion | null) => void;
}

/** "Promotion code": pick one of the public codes or type one; the discount itself comes from the fee. */
export default function PromotionModal({ open, toggle, online, siteCode, lang, current, onApplied }: IProps) {
  //! state
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.appCommonSlice.userInfo);
  const [list, setList] = useState<IPromotion[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [code, setCode] = useState<string>(current?.type ?? "");
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [viewing, setViewing] = useState<IPromotion>();
  const [applied, setApplied] = useState<IPromotion>();

  //! function
  useEffect(() => {
    const query = { lang, site_code: siteCode, category: categories.APPT };
    const request = online ? promotionService.getPromotionListPublicOnline(query) : promotionService.getPromotionListPublic(query);

    request
      .then((resp) => setList(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => setList([]))
      .finally(() => setIsLoading(false));
  }, [online, siteCode, lang]);

  const errorText = (applyError: unknown) => {
    const response = (applyError as AxiosError).response;

    return t(`booking.voucher.errors.${voucherError(response?.status, response?.data)}`);
  };

  const apply = async (promotion: IPromotion) => {
    try {
      setIsApplying(true);
      setError("");
      const resp = await promotionService.promotionApply(promotion.type, {
        lang,
        apply_date: dayjs().format("YYYY-MM-DD"),
        site_code: siteCode,
        category: categories.APPT,
        phone: userInfo?.user_name,
      });

      setApplied({ ...promotion, ...(resp.data && typeof resp.data === "object" ? resp.data : {}) });
    } catch (applyError) {
      setError(errorText(applyError));
    } finally {
      setIsApplying(false);
    }
  };

  /** A typed code is looked up first; one without a description has run out. */
  const applyTyped = async () => {
    const typed = code.trim().toUpperCase();

    if (!typed) {
      onApplied(null);
      toggle();
      return;
    }

    try {
      setIsApplying(true);
      setError("");
      const resp = await promotionService.promotionGetByType(typed, { lang, site_code: siteCode, category: categories.APPT });
      const promotion: IPromotion | undefined = resp.data;

      if (!promotion?.description) {
        setError(t("booking.voucher.errors.EXPIRED"));
        return;
      }

      await apply({ ...promotion, type: promotion.type || typed });
    } catch (lookupError) {
      setError(errorText(lookupError));
    } finally {
      setIsApplying(false);
    }
  };

  //! render
  const renderPromotion = (promotion: IPromotion) => (
    <div
      key={promotion.id ?? promotion.type}
      className={`flex items-center gap-3 p-3 rounded-lg border ${promotion.type === code ? "border-common-primary bg-common-primary50" : "border-common-divider"}`}
    >
      <div className="grow min-w-0 flex flex-col gap-0.5">
        <span className="text-common-maintext text-sm font-semibold font-['Inter']">{promotion.type}</span>
        <span className="text-common-maintext text-sm font-['Inter'] line-clamp-2">{promotion.name}</span>
        <span className="text-common-subtext text-xs font-['Inter']">{t("booking.voucher.expiry", { date: promotion.expired_date })}</span>
        {promotion.max_value > 0 ? (
          <span className="text-common-subtext text-xs font-['Inter']">
            {t("booking.voucher.maxValue", { value: formatVnd(promotion.max_value) })}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <button type="button" className="text-common-primary text-xs font-medium font-['Inter']" onClick={() => setViewing(promotion)}>
          {t("booking.voucher.detail")}
        </button>
        <CommonStyles.ButtonUI
          size="small"
          loading={isApplying && promotion.type === code}
          onClick={() => {
            setCode(promotion.type);
            apply(promotion);
          }}
        >
          {t("booking.voucher.apply")}
        </CommonStyles.ButtonUI>
      </div>
    </div>
  );

  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      width={640}
      title={t("booking.voucher.title")}
      content={
        applied ? (
          <div className="flex flex-col gap-3">
            <span className="text-common-maintext text-base font-semibold font-['Inter']">{applied.name}</span>
            {applied.description ? (
              <span className="text-common-subtext text-sm font-['Inter'] whitespace-pre-line">{applied.description}</span>
            ) : null}
            {applied.max_value > 0 ? (
              <span className="text-common-subtext text-sm font-['Inter']">
                {t("booking.voucher.maxValue", { value: formatVnd(applied.max_value) })}
              </span>
            ) : null}
            <div className="flex justify-end">
              <CommonStyles.ButtonUI
                className="h-12 px-6"
                onClick={() => {
                  onApplied(applied);
                  toggle();
                }}
              >
                {t("booking.agree")}
              </CommonStyles.ButtonUI>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <CommonStyles.InputUI
                variant="filled"
                allowClear
                value={code}
                placeholder={t("booking.voucher.placeholder")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value.toUpperCase())}
              />
              <CommonStyles.ButtonUI className="h-10 shrink-0" loading={isApplying} onClick={applyTyped}>
                {t("booking.voucher.apply")}
              </CommonStyles.ButtonUI>
            </div>

            {error ? <span className="text-common-red500 text-sm font-['Inter']">{error}</span> : null}

            {isLoading ? <CommonStyles.LoadingUI /> : null}

            <div className="flex flex-col gap-2 max-h-[calc(100vh-360px)] overflow-y-auto">{list.map(renderPromotion)}</div>

            {!isLoading && !list.length ? (
              <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-['Inter']">
                {t("booking.voucher.empty")}
              </div>
            ) : null}

            {viewing ? (
              <CommonStyles.ModalConfirmationUI
                open
                toggle={() => setViewing(undefined)}
                type="normal"
                hiddenAction
                title={viewing.name}
                content={<div className="text-common-maintext text-sm font-['Inter'] whitespace-pre-line">{viewing.description}</div>}
              />
            ) : null}
          </div>
        )
      }
    />
  );
}
